import api from './api'
import { ApiResponse, PagedResult } from '../types/api'
import { getApiBaseUrl } from '@utils/apiBaseUrl'

export type CompanyType = 'FORNECEDOR' | 'OFICINA'

export interface Company {
  id: number
  name: string
  address: string
  type: CompanyType
  idAdmin: string
  active?: boolean
  nif?: string
  phone?: string
  email?: string
}

export interface CompanyStats {
  totalSuppliers: number
  totalMechanics: number
  totalCustomers: number
  totalCompanies: number
}

export const companyService = {
  async getByType(
    type: CompanyType,
    params?: { page?: number; size?: number }
  ): Promise<PagedResult<Company>> {
    const response = await api.get<ApiResponse<PagedResult<Company>>>(`/companies/type/${type}`, {
      params,
    })

    return response.data.data
  },

  async getById(id: number): Promise<Company> {
    const response = await api.get<Company>(`/companies/${id}`)
    return response.data
  },

  async getDetails(id: number): Promise<Company> {
    const response = await api.get<ApiResponse<Company>>(`/companies/${id}/details`)
    return response.data.data
  },

  async getStats(): Promise<CompanyStats> {
    const response = await api.get<CompanyStats>('/companies/stats')
    return response.data
  },

  async update(
    id: number,
    data: { name: string; address: string; type: CompanyType; idAdmin: string; nif?: string; phone?: string }
  ): Promise<Company> {
    const response = await api.put<Company>(`/companies/${id}`, data)
    return response.data
  },

  async generatePdf(id: number, uploadedBy: number): Promise<{ message: string; fileUploadId?: number; filename?: string }> {
    const response = await api.post<ApiResponse<{ message: string; fileUploadId: number; filename: string } | string>>(`/companies/${id}/generate-pdf`, null, {
      params: { uploadedBy },
    })
    
    // Se a resposta tem a estrutura esperada (Map/objeto)
    if (response.data.data && typeof response.data.data === 'object' && !Array.isArray(response.data.data)) {
      const data = response.data.data as any
      return {
        message: data.message || 'PDF gerado com sucesso',
        fileUploadId: data.fileUploadId,
        filename: data.filename
      }
    }
    
    // Fallback: tentar extrair da mensagem (compatibilidade com versão antiga)
    const message = typeof response.data.data === 'string' ? response.data.data : (response.data.message || '')
    const idMatch = message.match(/FileUpload ID: (\d+)/)
    const fileUploadId = idMatch ? parseInt(idMatch[1], 10) : undefined
    return { message, fileUploadId }
  },

  async downloadPdfDirect(customerId: number): Promise<void> {
    // Download direto do PDF (gera e baixa sem salvar no sistema)
    const token = localStorage.getItem('token')
    if (!token) {
      throw new Error('Token de autenticação não encontrado')
    }
    
    const baseURL = getApiBaseUrl()

    const response = await fetch(`${baseURL}/companies/${customerId}/pdf`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Erro desconhecido')
      throw new Error(`Erro ao fazer download do PDF: ${response.status} ${response.statusText}. ${errorText}`)
    }
    
    const blob = await response.blob()
    if (blob.size === 0) {
      throw new Error('O arquivo PDF está vazio')
    }
    
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.style.display = 'none'
    
    const contentDisposition = response.headers.get('Content-Disposition')
    let filename = `Cliente-${customerId}.pdf`
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        let extractedFilename = filenameMatch[1]
        // Remove UTF-8'' prefix first (before removing quotes, as quotes are part of the prefix)
        extractedFilename = extractedFilename.replace(/^["']?UTF-8''/, '')
        // Then remove remaining quotes
        extractedFilename = extractedFilename.replace(/['"]/g, '')
        filename = extractedFilename
      }
    }
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    
    setTimeout(() => {
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }, 100)
  },

  async downloadPdf(fileUploadId: number): Promise<void> {
    // Usar fetch diretamente para suportar blob
    const token = localStorage.getItem('token')
    if (!token) {
      throw new Error('Token de autenticação não encontrado')
    }
    
    const baseURL = getApiBaseUrl()

    const response = await fetch(`${baseURL}/files/${fileUploadId}/download`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    })
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Erro desconhecido')
      throw new Error(`Erro ao fazer download do PDF: ${response.status} ${response.statusText}. ${errorText}`)
    }
    
    const blob = await response.blob()
    
    // Verificar se o blob não está vazio
    if (blob.size === 0) {
      throw new Error('O arquivo PDF está vazio')
    }
    
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.style.display = 'none'
    
    // Obter nome do arquivo do header Content-Disposition ou usar nome padrão
    const contentDisposition = response.headers.get('Content-Disposition')
    let filename = `Cliente-${fileUploadId}.pdf`
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '').replace(/^UTF-8''/, '')
      }
    }
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    
    // Limpar após um pequeno delay para garantir que o download iniciou
    setTimeout(() => {
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }, 100)
  },

  async getByNIF(nif: string): Promise<Company & { nif?: string; phone?: string; email?: string } | null> {
    try {
      // Backend retorna Map com dados completos incluindo nif, email, phone extraídos das work orders
      const response = await api.get<ApiResponse<Company & { nif?: string; phone?: string; email?: string }>>(`/companies/nif/${nif}`)
      return response.data.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  },
}





